import { cn } from '@buakei/ui';
import type { LucideIcon } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useAuth } from '@/auth/session';

/**
 * Minimal, assistant-first home (ADR-009): a greeting, one question, a few actions and a
 * verifiable trust line. Deliberately sparse; detail lives on the pages the actions open.
 */

export type HomeAction = {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Shown beside the label, e.g. "Preview" for a module that is not functional yet. */
  note?: string;
};

function greeting(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/** First name for real accounts; DEMO personas keep their full, clearly fictitious name. */
function nameFor(session: ReturnType<typeof useAuth>['session']): string | null {
  if (!session) return null;
  if (session.provider === 'demo') return session.displayName;
  const name = session.displayName.trim();
  if (!name || name === session.email || name === 'Your account') return null;
  return name.split(/\s+/)[0] ?? null;
}

export function AssistantHome({
  title,
  question,
  actions,
  trust,
  trustIcon: TrustIcon,
  children,
}: {
  /** Document title. */
  title: string;
  question: string;
  actions: HomeAction[];
  trust: string;
  trustIcon: LucideIcon;
  /** Optional quiet content under the trust line (e.g. a disclosure). */
  children?: ReactNode;
}) {
  const { session } = useAuth();
  const name = nameFor(session);
  const hello = greeting(new Date().getHours());

  useEffect(() => {
    document.title = `${title} · Buakei AI`;
  }, [title]);

  return (
    <div className="mx-auto flex min-h-[calc(100dvh-10rem)] max-w-3xl flex-col items-center justify-center gap-8 px-4 py-12 text-center">
      <div className="flex flex-col gap-3">
        <p className="text-lg text-muted-600">{name ? `${hello}, ${name}` : hello}</p>
        <h1 className="font-display text-4xl leading-tight text-navy-900 md:text-5xl">
          {question}
        </h1>
      </div>
      <nav aria-label="Start" className="w-full">
        <ul className="flex flex-wrap justify-center gap-3">
          {actions.map((action) => (
            <li key={action.to}>
              <Link
                to={action.to}
                className={cn(
                  'flex h-12 items-center gap-2 rounded-full border border-line-200 bg-white px-5',
                  'text-base font-medium text-navy-900 transition-colors',
                  'hover:border-navy-900/40 hover:bg-navy-50',
                )}
              >
                <action.icon className="size-5 shrink-0" aria-hidden />
                {action.label}
                {action.note ? (
                  <span className="text-xs font-normal text-muted-600">{action.note}</span>
                ) : null}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      <p className="flex items-center gap-2 text-sm text-muted-600">
        <TrustIcon className="size-4 shrink-0" aria-hidden /> {trust}
      </p>
      {children}
    </div>
  );
}
